import { camelCase, isArray, isObject, transform } from "lodash-es";
import { Mime } from "mime/lite";
import otherTypes from "mime/types/other.js";
import standardTypes from "mime/types/standard.js";
import { VNode, render } from "vue";

import config from "@/config";
import { FILENAME_EXTENSION_RE } from "@/lib/filename";
import { app } from "@/main";

/**
 * Create Intl.DateTimeFormat for showing long date time formats
 * Not all methods of DateTimeFormat have wide-spread browser support,
 * verify at https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 * before using.
 */
// TODO: i18n of format (set 'en-GB' based on set language)
// TODO: Add shortDateTimeFormat
export const longDateTimeFormat = Intl.DateTimeFormat(
    "en-GB",
    config.longDateTimeFormatOptions
);

// Validate the name of StekzModel instance (domain, product, process, application, service)
// Returns an array of error strings
export function validateName(name: string): string[] {
    const errors: string[] = [];
    if (!name.trim()) {
        errors.push("Missing name");
        return errors;
    }
    if (name.length > 69) {
        errors.push("Name should max be 69 characters");
    }
    return errors;
}

// Validate and clean FQDN, stripping any protocol/path
export function validateAndCleanFqdn(input: string): {
    value: string;
    error?: string;
} {
    if (!input) return { value: "" };

    try {
        // Handle URLs with protocol
        if (input.includes("://")) {
            const url = new URL(input);
            input = url.hostname;
        }

        // Basic FQDN validation
        const fqdnRegex =
            /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        if (!fqdnRegex.test(input)) {
            return {
                value: input,
                error: "Please enter a valid domain name (e.g., example.com)",
            };
        }
        return { value: input };
    } catch {
        return {
            value: input,
            error: "Please enter a valid domain name (e.g., example.com)",
        };
    }
}

// check for match values in object array
export function valexists(
    dataset: Array<object>,
    key: string,
    name: string
): boolean {
    return dataset.some((el) => {
        return el[key].toLowerCase() === name.toLowerCase();
    });
}

// Check if an object is empty
export function isEmpty(object: object): boolean {
    try {
        return (
            JSON.stringify(object) === undefined ||
            JSON.stringify(object) === "{}"
        );
    } catch {
        // Will throw an error if it has a circular structure, which means it isn't empty
        return false;
    }
}

// Check if an object exists AND is not empty
export function exists(object: object): boolean {
    return object && !isEmpty(object);
}

// Define some extra mime-types for modelling
export const mime = new Mime(standardTypes, otherTypes, {
    "application/schema+json": ["form", "schema.json"],
    "application/domain+xml": ["domain"],
    "application/dmn+xml": ["dmn"],
    "application/bpmn+xml": ["bpmn"],
    "application/vnd.google-apps.drawing": ["gdraw"],
    "text/x-feature": ["feature"],
});

// Legacy artefacts were persisted with their extension stripped, so their `name` is bare
// (e.g. `report` instead of `report.pdf`). Fall back to the mimetype's canonical extension
// when the filename has no extension-like suffix of its own.
export function ensureFilenameExtension(
    name: string,
    mimetype: string
): string {
    if (FILENAME_EXTENSION_RE.test(name)) {
        return name;
    }
    const extension = mime.getExtension(mimetype);
    return extension ? `${name}.${extension}` : name;
}

/**
 * Present a blobURL as downloadable file to the browser, this can be used in stead of directing
 * the user towards a downloadable link, since these requests might miss the required headers
 * for authentication. Inspired by https://github.com/kennethjiang/js-file-download/.
 *
 * @param filename the name as which the file gets saved
 */
export function downloadBlob(blobURL: string, filename: string) {
    const tempLink = document.createElement("a");
    tempLink.style.display = "none";
    tempLink.href = blobURL;
    tempLink.setAttribute("download", filename);
    document.body.appendChild(tempLink);
    tempLink.click();

    // Resolves "webkit blob resource error 1"
    setTimeout(() => {
        document.body.removeChild(tempLink);
        window.URL.revokeObjectURL(blobURL);
    }, 200);
}

/**
 * Run the given function only when it's not called for waitMilliseconds.
 */
export function debounce<Params extends unknown[]>(
    func: (...args: Params) => unknown,
    waitMilliseconds: number
): {
    (): void;
    cancel: () => void;
} {
    let timeout: NodeJS.Timeout | number | undefined;

    const Debounced = function executedFunction(...args: Params) {
        const later = () => {
            timeout = undefined;
            func.apply(this, args);
        };

        clearTimeout(timeout);
        timeout = setTimeout(later, waitMilliseconds);
    };

    Debounced.cancel = () => clearTimeout(timeout);

    return Debounced;
}

export function camelizeObjectKeys<T extends object>(obj: object): T {
    return transform<object, T>(obj, (acc, value, key, target) => {
        const camelKey = isArray(target) ? key : camelCase(key);
        acc[camelKey] = isObject(value) ? camelizeObjectKeys(value) : value;
    });
}

/**
 * Render a Vue VNode (created with `h()`) into an Element.
 */
export const vnodeToHtml = (vnode: VNode): HTMLElement => {
    const container = document.createElement("div");
    vnode.appContext = app._context;
    render(vnode, container);
    /**
     * We have to return the main container here. Otherwise
     * the component is not reactive to top-level v-if statements.
     * Sadly, this means that we actually inject an additional div
     * in between. We could possibly directly attach it to a given parent
     * to prevent a div from spawning in between.
     */
    return container as HTMLElement;
};

export const waitForElement = <T extends Element>(selector: string) => {
    return new Promise<T>((resolve) => {
        const element = document.querySelector<T>(selector);
        if (element) {
            return resolve(element);
        }

        const observer = new MutationObserver(() => {
            const foundElement = document.querySelector<T>(selector);
            if (foundElement) {
                observer.disconnect();
                resolve(foundElement);
            }
        });

        // If you get "parameter 1 is not of type 'Node'" error, see https://stackoverflow.com/a/77855838/492336
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    });
};
